import React, { FC } from 'react';
import { Box, Card, Container, fade, Grid, IconButton, makeStyles, Theme, Typography, useMediaQuery } from '@material-ui/core';
import { Separator } from 'components/atoms';
import {
  EmailContactIcon,
  FacebookContactIcon,
  LinkedinContactIcon,
  LocationContactIcon,
  PhoneContactIcon,
  PrimaryFacebookIcon,
  PrimaryLinkedInIcon,
} from 'svgs';
import { FACEBOOK_LINK, GOOGLE_MAP_LINK, LINKEDIN_LINK } from 'data/constants';
import clsx from 'clsx';
import { ContactUsForm } from 'templates/forms';

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    minHeight: 700,
    backgroundColor: theme.palette.secondary.main,
  },
  card: {
    position: 'relative',
    top: theme.spacing(-4),
    width: '100%',
    padding: theme.spacing(4),
    marginBottom: theme.spacing(-4),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(10),
      marginBottom: 0,
    },
  },
  container: {
    position: 'relative',
    paddingBottom: 0,
    [theme.breakpoints.up('sm')]: {
      paddingBottom: theme.spacing(16),
    },
  },
  infoBox: {
    display: 'flex',
    '& a:hover': {
      textDecoration: 'underline !important',
    },
  },
  formCard: {
    boxShadow: 'none',
    maxWidth: '100%',
    margin: '0',
    position: 'relative',
    overflow: 'visible',
    zIndex: 1,
    '& .MuiPaper-root': {
      boxShadow: 'inherit',
      padding: theme.spacing(16, 0, 0),
    },
    [theme.breakpoints.up('md')]: {
      maxWidth: theme.spacing(98),
      margin: '0 auto',
      boxShadow: `0px 4px 37px ${fade(theme.palette.common.black, 0.15)}`,
      '& .MuiPaper-root': {
        boxShadow: 'none !important',
        padding: theme.spacing(8),
      },
    },
    '& form': {
      paddingBottom: theme.spacing(2),
    },
  },
  square: {
    position: 'absolute',
    zIndex: -1,
    backgroundColor: theme.palette.primary.main,
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'block',
    },
  },
  topSquare: {
    top: theme.spacing(-2),
    left: theme.spacing(-2),
    height: 280,
    width: 210,
  },
  bottomSquare: {
    width: 210,
    height: 130,
    bottom: theme.spacing(-2),
    right: theme.spacing(-2),
  },
  socialButtons: {
    display: 'none',
    flexDirection: 'column',
    position: 'absolute',
    top: '30%',
    right: theme.spacing(-5),
    padding: theme.spacing(3, 1),
    backgroundColor: theme.palette.primary.main,
    [theme.breakpoints.up('md')]: {
      display: 'flex',
    },
    '& svg': {
      margin: theme.spacing(1, 0),
    },
  },
  icons: {
    '& svg': {
      margin: theme.spacing(0.5),
    },
  },
}));

const FormSection: FC = () => {
  const classes = useStyles();
  const isLarge = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  return (
    <Box className={classes.wrapper}>
      <Container disableGutters={!isLarge} fixed maxWidth={false} className={classes.container}>
        <Card className={classes.card} data-aos="fade-up">
          <Grid spacing={isLarge ? 6 : 0} container>
            <Grid md={4} sm={12} xs={12} item>
              <Typography data-aos="fade-down" data-aos-delay="200" color="primary" variant="h2" component="h2">
                <span>Get in Touch</span>
              </Typography>
              <Separator my={2} />
              <Typography data-aos="fade-down" data-aos-delay="250">
                Whenever it's a question or a valuable suggestion, leave us an inquiry, and we will get back to you shortly.
              </Typography>
              <Box data-aos="fade-down" data-aos-delay="200" mt={isLarge ? 10 : 12} className={classes.infoBox}>
                <LocationContactIcon />
                <Box ml={6}>
                  <Typography gutterBottom variant="subtitle1">
                    <span>Address</span>
                  </Typography>
                  <a href={GOOGLE_MAP_LINK} target="_blank" rel="noopener noreferrer">
                    <Typography>
                      1051 Perimeter Dr, <br /> Suite 510, Schaumburg, <br /> IL 60173
                    </Typography>
                  </a>
                </Box>
              </Box>
              {isLarge && <Box pt={9} />}
              <Box data-aos="fade-down" data-aos-delay="200" mt={10} className={classes.infoBox}>
                <EmailContactIcon />
                <Box ml={6}>
                  <Typography gutterBottom variant="subtitle1">
                    <span>Email</span>
                  </Typography>
                  <a href="mailto:info@patrontrucking.com">
                    <Typography>info@patrontrucking.com</Typography>
                  </a>
                </Box>
              </Box>
              {isLarge && <Box pt={9} />}
              <Box data-aos="fade-down" data-aos-delay="200" mt={10} className={classes.infoBox}>
                <PhoneContactIcon />
                <Box ml={6}>
                  <Typography gutterBottom variant="subtitle1">
                    <span>Phone</span>
                  </Typography>
                  <a href="tel:+14259999987">
                    <Typography>+1 (425) 999-9987</Typography>
                  </a>
                </Box>
              </Box>
            </Grid>
            <Grid md={8} sm={12} xs={12} item>
              <Box data-aos="zoom-in" data-aos-delay="300" className={classes.formCard}>
                <Card>
                  <Box className={clsx(classes.square, classes.topSquare)} />
                  <Box display="flex" justifyContent="space-between">
                    <Typography color="primary" variant="h2">
                      <span>Contact Us</span>
                    </Typography>
                    {!isLarge && (
                      <Box display="flex" className={classes.icons}>
                        <a href={FACEBOOK_LINK} target="_blank" rel="noopener noreferrer">
                          <PrimaryFacebookIcon />
                        </a>
                        <a href={LINKEDIN_LINK} target="_blank" rel="noopener noreferrer">
                          <PrimaryLinkedInIcon />
                        </a>
                      </Box>
                    )}
                  </Box>
                  <ContactUsForm />
                  <Box className={clsx(classes.square, classes.bottomSquare)} />
                  <Box className={classes.socialButtons}>
                    <a href={FACEBOOK_LINK} target="_blank" rel="noopener noreferrer">
                      <IconButton size="small">
                        <FacebookContactIcon />
                      </IconButton>
                    </a>
                    <a href={LINKEDIN_LINK} target="_blank" rel="noopener noreferrer">
                      <IconButton size="small">
                        <LinkedinContactIcon />
                      </IconButton>
                    </a>
                  </Box>
                </Card>
              </Box>
            </Grid>
          </Grid>
        </Card>
      </Container>
    </Box>
  );
};

export default FormSection;
